/**
 * Lines typed while a live call reconnects (docs/plans/os-voice-conversation.md,
 * rule 24: "Reconnecting · typing still works"), for both clients.
 *
 * A line typed while the socket is away waits here, and the call's `say`
 * answers with the line's own promise: true once the call is back and its
 * socket took the line, false when the socket would not, or when the call
 * ended before it was back (the grace ran out, Stop live, a new start). On
 * false the caller sends the line the typed way, as it does for any line the
 * call could not take, so a line typed while Reconnecting is never lost.
 */
export type SayLater = {
  /** Keep a line until the call is back; answers whether it went out through the call. */
  readonly hold: (text: string) => Promise<boolean>;
  /** The call is back: each line goes in order through `sendOne`, which answers whether the socket took it. */
  readonly send: (sendOne: (text: string) => boolean) => void;
  /** The call ended before it was back: every line answers not sent. */
  readonly drop: () => void;
  /** How many lines wait. */
  readonly size: () => number;
};

export const createSayLater = (): SayLater => {
  let waiting: { readonly text: string; readonly settle: (sent: boolean) => void }[] = [];
  const take = (): typeof waiting => {
    const held = waiting;
    waiting = [];
    return held;
  };
  return {
    hold: (text) => new Promise<boolean>((settle) => waiting.push({ text, settle })),
    send: (sendOne) => {
      for (const line of take()) line.settle(sendOne(line.text));
    },
    drop: () => {
      for (const line of take()) line.settle(false);
    },
    size: () => waiting.length,
  };
};
